import { FileText, Loader2, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import PrdDocumentModal from '@/components/PrdDocumentModal';
import { getConfirmDocLabels, confirmDocFilename } from '@/lib/confirmDocLabels';
import { cn } from '@/lib/utils';

/**
 * Confirm document artifact (PRD / report spec / auth ACR).
 * variant="banner" — compact full-width strip for the chat header.
 * variant="card" (default) — stacked card for side rails.
 */
export default function PrdArtifactCard({
  sessionId,
  phase,
  prdAvailable,
  prdChecking,
  intent,
  confirmDocType,
  className,
  variant = 'card',
  defaultExpanded = true,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!sessionId) {
    return null;
  }

  const labels = getConfirmDocLabels({ intent, confirmDocType });
  const docLabel = confirmDocFilename(sessionId, labels);
  // Spinner only while actively probing before the parent marks the doc ready.
  const preparing = !prdAvailable && prdChecking;

  if (variant === 'banner') {
    return (
      <div
        className={cn(
          'overflow-hidden rounded-xl border border-cp-mod/80 bg-gradient-to-r from-cp-inset to-cp-surface shadow-sm',
          className
        )}
        data-testid="prd-artifact-banner"
      >
        <button
          type="button"
          className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-cp-inset/50"
          onClick={() => setExpanded((o) => !o)}
          aria-expanded={expanded}
          data-testid="prd-artifact-banner-toggle"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cp-mod/10 text-cp-mod">
            {preparing && !prdAvailable ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-cp-fg">{labels.title}</p>
            {!expanded && (
              <p className="truncate text-xs text-cp-mod/80">
                {prdAvailable ? docLabel : preparing ? labels.preparing : labels.empty}
              </p>
            )}
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-cp-muted transition-transform duration-200',
              expanded && 'rotate-180'
            )}
          />
        </button>
        {expanded && (
          <div className="flex flex-col gap-3 border-t border-cp-mod/40 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs text-cp-mod/80">{labels.description}</p>
              {prdAvailable ? (
                <p className="mt-1 truncate text-xs font-medium text-cp-fg">{docLabel}</p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  {preparing ? labels.preparing : labels.empty}
                </p>
              )}
            </div>
            {prdAvailable && (
              <div className="shrink-0 sm:pl-2" onClick={(e) => e.stopPropagation()}>
                <PrdDocumentModal
                  key={sessionId}
                  sessionId={sessionId}
                  triggerVariant="button"
                  labels={labels}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className={cn('overflow-hidden border-cp-mod/90 shadow-md', className)}>
      <CardHeader className="border-b border-cp-line bg-gradient-to-r from-cp-inset to-cp-surface pb-3 pt-4">
        <CardTitle className="flex items-center gap-2 text-base text-cp-fg">
          <FileText className="h-5 w-5" />
          {labels.title}
        </CardTitle>
        <CardDescription className="text-xs text-cp-mod/80">{labels.description}</CardDescription>
      </CardHeader>
      <CardContent className="p-4">
        {prdAvailable ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-cp-mod/80 bg-cp-inset/50 p-4">
              <div className="mb-2 text-sm text-muted-foreground">Document</div>
              <div className="flex items-start gap-2 break-all text-cp-fg">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-cp-mod" />
                <span className="text-sm font-medium">{docLabel}</span>
              </div>
            </div>
            <PrdDocumentModal
              key={sessionId}
              sessionId={sessionId}
              triggerVariant="button"
              labels={labels}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-cp-mod/80 bg-cp-inset/30 py-8 text-center">
            {preparing ? (
              <>
                <Loader2 className="mx-auto mb-3 h-10 w-10 animate-spin text-cp-mod" />
                <p className="text-sm font-medium text-cp-fg">{labels.preparing}</p>
                <p className="mt-1 px-4 text-xs text-cp-mod/70">
                  The document is generated when you reach confirmation. It will appear here when ready.
                </p>
              </>
            ) : (
              <>
                <FileText className="mx-auto mb-3 h-10 w-10 text-cp-mod" />
                <p className="text-sm text-muted-foreground">{labels.empty}</p>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
